import React, { useEffect, useState } from 'react';
const styles = {
    wrapper: {
        fontFamily: "'Outfit', sans-serif",
        backgroundColor: '#f4f6f8',
        color: '#1a1a1a',
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: '40px 20px',
        boxSizing: 'border-box'
    },
    container: {
        maxWidth: '800px',
        width: '100%',
        backgroundColor: '#ffffff',
        borderRadius: '16px',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
        border: '1px solid #e1e4e8',
        overflow: 'hidden',
        position: 'relative'
    },
    header: {
        padding: '20px 40px',
        borderBottom: '1px solid #e1e4e8',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        background: '#ffffff'
    },
    logoContainer: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px'
    },
    logoText: {
        color: '#28a745', // Green color
        fontWeight: 700,
        fontSize: '1.5rem',
        letterSpacing: '1px'
    },
    toolbar: {
        display: 'flex',
        gap: '10px'
    },
    button: {
        padding: '8px 16px',
        borderRadius: '6px',
        border: '1px solid #d1d5db',
        backgroundColor: '#f8f9fa',
        color: '#333',
        cursor: 'pointer',
        fontSize: '0.85rem',
        fontWeight: 500,
        transition: 'all 0.2s ease',
        display: 'flex',
        alignItems: 'center',
        gap: '6px'
    },
    metaSection: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '1px',
        backgroundColor: '#eaeaea',
        borderBottom: '1px solid #eaeaea'
    },
    metaItem: {
        backgroundColor: '#ffffff',
        padding: '20px 40px'
    },
    metaLabel: {
        fontSize: '0.75rem',
        textTransform: 'uppercase',
        color: '#6c757d',
        fontWeight: 600,
        marginBottom: '6px',
        display: 'block'
    },
    metaValue: {
        fontSize: '1rem',
        fontWeight: 500,
        color: '#212529'
    },
    contentArea: {
        padding: '40px',
        minHeight: '200px'
    },
    transcriptionText: {
        lineHeight: 1.8,
        fontSize: '1.1rem',
        color: '#2c3e50',
        whiteSpace: 'pre-wrap',
        fontFamily: "'Inter', system-ui, sans-serif"
    },
    footer: {
        marginTop: 'auto',
        paddingTop: '40px',
        textAlign: 'center',
        color: '#6c757d',
        fontSize: '0.9rem',
        borderTop: '1px solid #e1e4e8',
        width: '100%',
        paddingBottom: '20px'
    },
    poweredBy: {
        fontWeight: 600,
        color: '#495057'
    },
    accordion: {
        width: '100%',
        backgroundColor: '#fff'
    },
    accordionItem: {
        borderBottom: '1px solid #e1e4e8'
    },
    accordionHeader: {
        padding: '24px 40px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        cursor: 'pointer',
        backgroundColor: '#fff',
        transition: 'background-color 0.2s ease',
        userSelect: 'none'
    },
    accordionTitle: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        fontSize: '1.2rem',
        fontWeight: 600,
        color: '#1a1a1a'
    },
    accordionIcon: {
        transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        color: '#6c757d'
    },
    accordionContent: {
        maxHeight: '0',
        overflow: 'hidden',
        transition: 'max-height 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        backgroundColor: '#fcfcfc'
    },
    accordionContentOpen: {
        maxHeight: '2000px', // Large enough for content
        padding: '0 40px 40px',
        overflow: 'auto'
    },
    summaryWrapper: {
        backgroundColor: 'rgba(237, 28, 36, 0.05)',
        borderRadius: '12px',
        padding: '25px',
        borderLeft: '4px solid #ed1c24',
        marginTop: '10px'
    },
    summaryContent: {
        lineHeight: 1.7,
        fontSize: '1.05rem',
        color: '#333'
    },
    transcriptionWrapper: {
        marginTop: '20px'
    },
    statusIndicator: {
        display: 'inline-flex',
        alignItems: 'center',
        padding: '8px 16px',
        borderRadius: '30px',
        fontSize: '0.9rem',
        fontWeight: 500,
        backgroundColor: '#e8f5e9',
        color: '#2e7d32',
        border: '1px solid #c8e6c9'
    }
};
const TranscriptionPage = () => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeSections, setActiveSections] = useState({
        summary: true,
        transcription: false
    });
    const toggleSection = (section) => {
        setActiveSections(prev => ({
            ...prev,
            [section]: !prev[section]
        }));
    };
    const meetingUuid = window.location.pathname.split('/').pop();
    useEffect(() => {
        if (!meetingUuid) {
            setError('Invalid meeting UUID.');
            setLoading(false);
            return;
        }
        // Determine backend URL with smart fallback
        let backendUrl = (typeof process !== 'undefined' ? process.env?.REACT_APP_BACKEND_URL : undefined)
            || window.config?.backendUrl;
        if (!backendUrl || (backendUrl.includes('localhost') && !window.location.hostname.includes('localhost'))) {
            backendUrl = window.location.origin;
        }
        const finalBackendUrl = backendUrl.endsWith('/') ? backendUrl.slice(0, -1) : backendUrl;
        // Inject marked.js for markdown rendering
        if (!window.marked) {
            const script = document.createElement('script');
            script.src = "https://cdn.jsdelivr.net/npm/marked/marked.min.js";
            script.async = true;
            document.body.appendChild(script);
        }
        // Fetch from the API endpoint
        fetch(`${finalBackendUrl}/api/transcription/${meetingUuid}`)
            .then(res => {
            if (!res.ok) {
                throw new Error('Transcription not found or processing.');
            }
            return res.json();
        })
            .then((jsonData) => {
            setData(jsonData);
            setLoading(false);
        })
            .catch(err => {
            console.error(err);
            // Remote log for debugging
            fetch('/api/logs', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    level: 'error',
                    message: `Transcription fetch failed for ${meetingUuid}`,
                    error: err.message,
                    url: window.location.href
                })
            }).catch(() => { });
            setError(err.message);
            setLoading(false);
        });
    }, [meetingUuid]);
    const parseTranscription = (rawText) => {
        if (!rawText)
            return '';
        if (rawText.trim().startsWith("{") && rawText.includes("'transcription':")) {
            try {
                const match = rawText.match(/'transcription':\s*'((?:[^'\\]|\\.)*)'/);
                if (match && match[1]) {
                    return match[1];
                }
            }
            catch (e) {
                console.warn("Failed to parse transcription string", e);
            }
        }
        return rawText;
    };
    const displayText = data ? parseTranscription(data.transcription) : '';
    const downloadWord = () => {
        if (!displayText)
            return;
        const htmlContent = `
            <html xmlns:o='urn:schemas-microsoft-com:office:office' xmlns:w='urn:schemas-microsoft-com:office:word' xmlns='http://www.w3.org/TR/REC-html40'>
            <head><meta charset='utf-8'><title>Transcription</title></head>
            <body>
                <h1>Meeting Transcription</h1>
                <p><strong>Meeting ID:</strong> ${meetingUuid}</p>
                <p><strong>Date:</strong> ${new Date().toLocaleDateString()}</p>
                <br/>
                <p>${displayText.replace(/\n/g, '<br/>')}</p>
            </body>
            </html>
        `;
        const blob = new Blob(['\ufeff', htmlContent], {
            type: 'application/msword'
        });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `transcription-${meetingUuid}.doc`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };
    const printPdf = () => {
        window.print();
    };
    const styleTag = `
        @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;600;700&family=Inter:wght@300;400;500&display=swap');
        
        @keyframes pulse {
            0% { opacity: 0.5; box-shadow: 0 0 0 0 rgba(237, 28, 36, 0.4); }
            70% { opacity: 1; box-shadow: 0 0 0 6px rgba(237, 28, 36, 0); }
            100% { opacity: 0.5; box-shadow: 0 0 0 0 rgba(237, 28, 36, 0); }
        }

        .btn-hover:hover {
            background-color: #333 !important;
            border-color: #333 !important;
            color: #ffffff !important;
        }

        @media print {
            body { background-color: white !important; color: black !important; }
            .no-print { display: none !important; }
            .print-container { 
                box-shadow: none !important; 
                border: none !important; 
                width: 100% !important; 
                max-width: none !important;
                margin: 0 !important;
                padding: 0 !important;
            }
            .header, .footer { border: none !important; background: none !important; }
            .logo-text { color: red !important; }
            .transcription-text { color: black !important; font-size: 12pt !important; }
        }
    `;
    return (React.createElement("div", { style: styles.wrapper },
        React.createElement("style", null, styleTag),
        React.createElement("div", { className: "print-container", style: styles.container },
            React.createElement("header", { className: "header", style: styles.header },
                React.createElement("div", { style: styles.logoContainer },
                    React.createElement("img", { src: "/images/npontu-logo.png", alt: "Npontu Logo", style: { height: '40px', marginRight: '15px', display: 'block' }, onError: (e) => { e.target.style.display = 'none'; } }),
                    React.createElement("span", { className: "logo-text", style: styles.logoText }, "NPONTU MEET")),
                React.createElement("div", { className: "toolbar no-print", style: styles.toolbar },
                    React.createElement("button", { className: "btn-hover", style: styles.button, onClick: printPdf, disabled: !displayText },
                        React.createElement("svg", { width: "16", height: "16", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round" },
                            React.createElement("path", { d: "M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" }),
                            React.createElement("polyline", { points: "14 2 14 8 20 8" }),
                            React.createElement("line", { x1: "16", y1: "13", x2: "8", y2: "13" }),
                            React.createElement("line", { x1: "16", y1: "17", x2: "8", y2: "17" }),
                            React.createElement("polyline", { points: "10 9 9 9 8 9" })),
                        "PDF"),
                    React.createElement("button", { className: "btn-hover", style: styles.button, onClick: downloadWord, disabled: !displayText },
                        React.createElement("svg", { width: "16", height: "16", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round" },
                            React.createElement("path", { d: "M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" }),
                            React.createElement("polyline", { points: "14 2 14 8 20 8" })),
                        "Word"))),
            React.createElement("div", { className: "meta-section", style: styles.metaSection },
                React.createElement("div", { className: "meta-item", style: styles.metaItem },
                    React.createElement("span", { style: styles.metaLabel }, "Meeting ID"),
                    React.createElement("span", { style: styles.metaValue }, meetingUuid)),
                React.createElement("div", { className: "meta-item", style: styles.metaItem },
                    React.createElement("span", { style: styles.metaLabel }, "Status"),
                    React.createElement("span", { style: styles.metaValue }, loading ? 'Syncing...' : ((data?.transcription && data?.summary) ? (React.createElement("div", { style: { display: 'flex', alignItems: 'center', gap: '6px' } },
                        React.createElement("div", { style: { width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#28a745' } }),
                        "Completed")) : (React.createElement("div", { style: { display: 'flex', alignItems: 'center', gap: '6px' } },
                        React.createElement("div", { style: {
                                width: '8px',
                                height: '8px',
                                borderRadius: '50%',
                                backgroundColor: '#ffc107',
                                animation: 'pulse 1.5s infinite'
                            } }),
                        "Meeting ongoing")))))),
            React.createElement("div", { className: "accordion-groups", style: styles.accordion },
                React.createElement("div", { className: "accordion-item", style: styles.accordionItem },
                    React.createElement("div", { style: {
                            ...styles.accordionHeader,
                            borderLeft: activeSections.summary ? '4px solid #ed1c24' : '4px solid transparent'
                        }, onClick: () => toggleSection('summary') },
                        React.createElement("div", { style: styles.accordionTitle },
                            React.createElement("svg", { width: "20", height: "20", viewBox: "0 0 24 24", fill: "none", stroke: "#ed1c24", strokeWidth: "2.5", strokeLinecap: "round", strokeLinejoin: "round" },
                                React.createElement("path", { d: "M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" })),
                            "Executive Summary"),
                        React.createElement("div", { style: {
                                ...styles.accordionIcon,
                                transform: activeSections.summary ? 'rotate(180deg)' : 'rotate(0)'
                            } },
                            React.createElement("svg", { width: "24", height: "24", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round" },
                                React.createElement("polyline", { points: "6 9 12 15 18 9" })))),
                    React.createElement("div", { style: {
                            ...styles.accordionContent,
                            ...(activeSections.summary ? styles.accordionContentOpen : {})
                        } },
                        React.createElement("div", { style: styles.summaryWrapper }, loading ? (React.createElement("div", { style: { color: '#666', fontStyle: 'italic' } }, "Distilling meeting intelligence...")) : (React.createElement("div", { style: styles.summaryContent }, data?.summary ? (React.createElement("div", { dangerouslySetInnerHTML: {
                                __html: window.marked
                                    ? window.marked.parse(data.summary)
                                    : data.summary
                            } })) : (error ? "Summary unavailable." : "Summary is being generated using Snwolley AI...")))))),
                React.createElement("div", { className: "accordion-item", style: styles.accordionItem },
                    React.createElement("div", { style: {
                            ...styles.accordionHeader,
                            borderLeft: activeSections.transcription ? '4px solid #28a745' : '4px solid transparent'
                        }, onClick: () => toggleSection('transcription') },
                        React.createElement("div", { style: styles.accordionTitle },
                            React.createElement("svg", { width: "20", height: "20", viewBox: "0 0 24 24", fill: "none", stroke: "#28a745", strokeWidth: "2.5", strokeLinecap: "round", strokeLinejoin: "round" },
                                React.createElement("path", { d: "M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" }),
                                React.createElement("polyline", { points: "14 2 14 8 20 8" }),
                                React.createElement("line", { x1: "16", y1: "13", x2: "8", y2: "13" }),
                                React.createElement("line", { x1: "16", y1: "17", x2: "8", y2: "17" }),
                                React.createElement("polyline", { points: "10 9 9 9 8 9" })),
                            "Full Transcription"),
                        React.createElement("div", { style: {
                                ...styles.accordionIcon,
                                transform: activeSections.transcription ? 'rotate(180deg)' : 'rotate(0)'
                            } },
                            React.createElement("svg", { width: "24", height: "24", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round" },
                                React.createElement("polyline", { points: "6 9 12 15 18 9" })))),
                    React.createElement("div", { style: {
                            ...styles.accordionContent,
                            ...(activeSections.transcription ? styles.accordionContentOpen : {})
                        } },
                        React.createElement("div", { style: styles.transcriptionWrapper },
                            loading && (React.createElement("div", { className: "status-indicator", style: styles.statusIndicator }, "Processing meeting audio...")),
                            !loading && error && (React.createElement("div", { style: { color: '#d32f2f' } }, error)),
                            !loading && !error && (React.createElement("div", { style: styles.transcriptionText }, displayText || "AI is processing your meeting transcript. Please check back in a moment..."))))))),
        React.createElement("footer", { className: "footer no-print", style: styles.footer },
            "Powered by ",
            React.createElement("span", { style: styles.poweredBy }, "Snwolley AI"))));
};
export default TranscriptionPage;
